import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../gql/client';
import { GET_IMAGE_BY_ID } from '../gql/queries/accidents/accidents';

// Types
import type { ImageData } from '../components/display/ResumeLoadImages/types';

/**
 * Resuelve registros `Image` a partir de sus ids, bajo demanda.
 *
 * ─── Por qué una a una ─────────────────────────────────────────────────────
 *
 * No existe una query por lote, y la alternativa (`images`) se trae TODAS las
 * del tenant. Es el mismo camino que ya usa el resumen de la actuación. Un
 * tramo tiene unas pocas fotos y esto solo corre al DESPLEGAR su card, así que
 * el coste es despreciable; con `cache-first`, desplegarla dos veces no repite
 * ni una petición.
 *
 * ─── Por qué aquí y no en el loader ────────────────────────────────────────
 *
 * El loader de «Órdenes asociadas» se adelgazó a propósito a una sola query
 * `cache-first` porque el pesado bloqueaba el cambio de pestaña. Resolver esto
 * al desplegar mantiene esa decisión.
 */
export function useImagesByIds(
  ids: string[] | undefined,
  enabled: boolean
): ImageData[] {
  const [images, setImages] = useState<ImageData[]>([]);
  const key = (ids ?? []).join(',');

  useEffect(() => {
    if (!enabled || !key) {
      setImages([]);
      return;
    }
    let cancelled = false;

    void (async () => {
      try {
        const results = await Promise.all(
          key.split(',').map((id) =>
            client.query({
              query: GET_IMAGE_BY_ID,
              variables: { id },
              fetchPolicy: 'cache-first',
            })
          )
        );
        if (cancelled) return;
        setImages(
          results
            .map((r) => r.data?.image as ImageData | undefined)
            .filter((img): img is ImageData => Boolean(img))
        );
      } catch (err) {
        // Sin cobertura no hay fotos que enseñar, pero la card debe seguir
        // funcionando: el botón de añadir no depende de esto.
        Sentry.captureException(err);
        if (!cancelled) setImages([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, key]);

  return images;
}

export default useImagesByIds;
